import { useState, useEffect } from "react";
import { useNavigate, Link } from "react-router-dom";
import { Auth, Orders, Wallet, auth } from "../lib/api.js";
import { VerifyModal } from "../components/UI.jsx";

const C = {
  navy: "#0E1A2E", navyDeep: "#070D1B", red: "#C8302A",
  amber: "#E8A317", green: "#0F8B3D", greenPale: "#E8F5E9",
  white: "#FFFFFF", cream: "#FAF7F1", sand: "#F0EADB",
  border: "#E0D9C8", line: "#EBE4D2",
  text: "#1A1A1A", text2: "#3A3A3A", text3: "#6A6A6A", text4: "#9A9A9A",
};

export default function Dashboard() {
  const navigate = useNavigate();
  const [merchant, setMerchant] = useState(null);
  const [walletBalance, setWalletBalance] = useState(null);
  const [orders, setOrders] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  useEffect(() => {
    if (!auth.loggedIn()) {
      navigate("/login");
      return;
    }
    loadAll();
  }, []); // eslint-disable-line

  async function loadAll() {
    setLoading(true);
    setError(null);
    try {
      const [me, balance, orderList] = await Promise.allSettled([
        Auth.me(),
        Wallet.balance(),
        Orders.list({ page_size: 10 }),
      ]);
      if (me.status === "fulfilled") setMerchant(me.value);
      if (balance.status === "fulfilled") setWalletBalance(balance.value);
      if (orderList.status === "fulfilled") {
        setOrders(orderList.value?.orders || orderList.value || []);
      }
      // If profile load failed with 401, the api.js wrapper already cleared the token
      if (me.status === "rejected" && me.reason?.status === 401) {
        navigate("/login");
      }
    } catch (err) {
      setError(err.message);
    } finally {
      setLoading(false);
    }
  }

  const logout = async () => {
    await Auth.logout();
    navigate("/");
  };

  if (loading) {
    return (
      <div style={{
        minHeight: "100vh", background: C.cream,
        display: "flex", alignItems: "center", justifyContent: "center",
      }}>
        <div style={{ color: C.text3, fontSize: 14 }}>Loading your dashboard...</div>
      </div>
    );
  }

  const hasOrders = orders.length > 0;
  const balanceRs = walletBalance?.balance_paise != null
    ? Math.round(walletBalance.balance_paise / 100)
    : 0;
  const merchantName = merchant?.business_name || merchant?.full_name || "there";

  return (
    <div style={{
      minHeight: "100vh", background: C.cream,
      fontFamily: "-apple-system, system-ui, Inter, sans-serif",
      color: C.text,
    }}>
      {/* Top bar */}
      <div style={{
        background: C.white, borderBottom: `1px solid ${C.line}`,
        padding: "16px 24px", position: "sticky", top: 0, zIndex: 50,
      }}>
        <div style={{ maxWidth: 1200, margin: "0 auto", display: "flex", justifyContent: "space-between", alignItems: "center" }}>
          <Link to="/" style={{ textDecoration: "none", display: "flex", alignItems: "center", gap: 10 }}>
            <div style={{
              width: 32, height: 32, background: C.red, borderRadius: 8,
              display: "flex", alignItems: "center", justifyContent: "center",
              color: C.white, fontWeight: 800, fontSize: 16, letterSpacing: -1,
            }}>S</div>
            <div style={{ fontSize: 17, fontWeight: 800, color: C.navy, letterSpacing: -0.5 }}>Shiftswift</div>
          </Link>
          <div style={{ display: "flex", alignItems: "center", gap: 20 }}>
            <span style={{ fontSize: 13, color: C.text3 }}>
              Hi, <strong style={{ color: C.navy }}>{merchantName}</strong>
            </span>
            <button onClick={logout} style={{
              background: "transparent", border: `1px solid ${C.border}`,
              padding: "8px 14px", borderRadius: 6, fontSize: 13, fontWeight: 600,
              color: C.text2, cursor: "pointer",
            }}>
              Sign out
            </button>
          </div>
        </div>
      </div>

      {/* Main */}
      <div style={{ maxWidth: 1200, margin: "0 auto", padding: "32px 24px" }}>
        {error && (
          <div style={{
            background: "#FEF2F2", border: "1px solid #FECACA", color: "#991B1B",
            padding: 14, borderRadius: 8, fontSize: 13, marginBottom: 24,
          }}>
            Some data couldn't be loaded: {error}
          </div>
        )}

        {/* KYC status banner — unmissable prompt for new merchants */}
        {merchant?.status === "pending_kyc" && (
          <div style={{
            background: "#FFF7ED", border: "1px solid #FED7AA",
            borderRadius: 10, padding: 18, marginBottom: 24,
            display: "flex", alignItems: "center", justifyContent: "space-between",
            flexWrap: "wrap", gap: 12,
          }}>
            <div>
              <div style={{ fontWeight: 700, color: "#9A3412", fontSize: 14 }}>
                ⚠ KYC required before you can book shipments
              </div>
              <div style={{ fontSize: 13, color: "#C2410C", marginTop: 4 }}>
                Upload your Aadhaar + PAN. Takes 2 minutes. Account activated within 24 hours.
              </div>
            </div>
            <Link to="/kyc" style={{
              background: "#EA580C", color: C.white,
              padding: "9px 18px", borderRadius: 7,
              textDecoration: "none", fontSize: 13, fontWeight: 700,
              flexShrink: 0,
            }}>
              Complete KYC →
            </Link>
          </div>
        )}
        {merchant?.status === "kyc_submitted" && (
          <div style={{
            background: "#EFF6FF", border: "1px solid #BFDBFE",
            borderRadius: 10, padding: 16, marginBottom: 24,
            fontSize: 13, color: "#1E40AF",
          }}>
            <strong>KYC under review</strong> — your documents are with our team. We'll activate your account within 24 hours and email you at {merchant.email}.
          </div>
        )}

        {/* Hero greeting */}
        <div style={{ marginBottom: 32 }}>
          <h1 style={{ fontSize: 28, fontWeight: 800, color: C.navy, margin: 0, letterSpacing: -0.8 }}>
            {hasOrders ? `Welcome back, ${merchantName.split(" ")[0]}.` : `Welcome, ${merchantName.split(" ")[0]}.`}
          </h1>
          <p style={{ fontSize: 15, color: C.text3, marginTop: 6 }}>
            {hasOrders
              ? "Here's what's happening with your shipments."
              : "Let's get you ready to ship your first parcel."}
          </p>
        </div>

        {/* Stats row */}
        <div style={{
          display: "grid",
          gridTemplateColumns: "repeat(auto-fit, minmax(220px, 1fr))",
          gap: 14, marginBottom: 32,
        }}>
          <StatCard label="Wallet balance" value={`₹${balanceRs.toLocaleString("en-IN")}`} sub="Available for shipping" accent={C.red} />
          <StatCard label="Total orders" value={orders.length} sub={hasOrders ? "All time" : "No orders yet"} accent={C.navy} />
          <StatCard label="Account status" value={merchant?.status === "active" ? "Active" : "Pending KYC"} sub={merchant?.status === "active" ? "Ready to ship" : "Submit KYC documents"} accent={merchant?.status === "active" ? C.green : C.amber} />
          <StatCard label="Plan" value="Starter" sub="Up to 500/month" accent={C.text3} />
        </div>

        {/* Onboarding checklist (for new merchants) OR Recent orders */}
        {!hasOrders ? (
          <OnboardingChecklist merchant={merchant} walletBalance={walletBalance} onReload={loadAll} />
        ) : (
          <RecentOrders orders={orders} />
        )}

        {/* Quick actions row */}
        <div style={{ marginTop: 32 }}>
          <div style={{ fontSize: 13, color: C.red, letterSpacing: 1.5, fontWeight: 700, marginBottom: 12 }}>
            QUICK ACTIONS
          </div>
          <div style={{
            display: "grid",
            gridTemplateColumns: "repeat(auto-fit, minmax(200px, 1fr))",
            gap: 12,
          }}>
            <ActionTile title="Book a shipment" desc="Express, standard, international" to="/ship" disabled={balanceRs === 0 && !hasOrders} />
            <ActionTile title="Recharge wallet" desc="UPI, card, or net banking" to="/recharge" />
            <ActionTile title="Bulk upload" desc="Up to 200 orders by CSV" to="/bulk" />
            <ActionTile title="Pickup addresses" desc="Manage warehouse locations" to="/pickups" />
            <ActionTile title="Pickup addresses" desc="Where India Post picks up from" to="/pickups" />
            <ActionTile title="Track shipment" desc="By AWB or order number" to="/track" />
          </div>
        </div>
      </div>
    </div>
  );
}

function StatCard({ label, value, sub, accent }) {
  return (
    <div style={{
      background: C.white, borderRadius: 12, padding: 20,
      border: `1px solid ${C.line}`,
    }}>
      <div style={{ fontSize: 11, color: C.text3, letterSpacing: 1, fontWeight: 700, textTransform: "uppercase" }}>
        {label}
      </div>
      <div style={{ fontSize: 28, fontWeight: 800, color: accent, marginTop: 8, letterSpacing: -0.5 }}>
        {value}
      </div>
      <div style={{ fontSize: 12, color: C.text3, marginTop: 4 }}>
        {sub}
      </div>
    </div>
  );
}

function OnboardingChecklist({ merchant, walletBalance, onReload }) {
  const [verifyModal, setVerifyModal] = useState(null); // null | "phone" | "email"
  const [hasPickup, setHasPickup] = useState(null); // null = loading
  const phoneVerified = !!merchant?.phone_verified;
  const emailVerified = !!merchant?.email_verified;
  const kycComplete = merchant?.status === "active";
  const walletFunded = (walletBalance?.balance_paise || 0) >= 10000;

  useEffect(() => {
    fetch("/api/v1/pickups/addresses", {
      headers: { Authorization: `Bearer ${auth.get()}` },
    })
      .then((r) => r.ok ? r.json() : [])
      .then((data) => setHasPickup(Array.isArray(data) && data.length > 0))
      .catch(() => setHasPickup(false));
  }, [merchant]);

  const steps = [
    { id: "register", label: "Account created", done: true },
    { id: "phone", label: "Verify phone number", done: phoneVerified, action: "Verify", onClick: () => setVerifyModal("phone") },
    { id: "email", label: "Verify email address", done: emailVerified, action: "Verify", onClick: () => setVerifyModal("email") },
    { id: "kyc", label: "Complete KYC verification", done: kycComplete, action: "Upload documents", to: "/kyc" },
    { id: "pickup", label: "Add a pickup address", done: hasPickup === true, action: "Add address", to: "/pickups" },
    { id: "wallet", label: "Recharge wallet (min ₹100)", done: walletFunded, action: "Add money", to: "/recharge" },
    { id: "ship", label: "Book your first shipment", done: false, action: "Book now", to: "/ship", disabled: !walletFunded || !hasPickup },
  ];
  const completed = steps.filter((s) => s.done).length;

  return (
    <div style={{
      background: C.white, borderRadius: 12, padding: 28,
      border: `1px solid ${C.line}`,
    }}>
      <div style={{ display: "flex", justifyContent: "space-between", alignItems: "flex-start", marginBottom: 20 }}>
        <div>
          <div style={{ fontSize: 13, color: C.red, letterSpacing: 1.5, fontWeight: 700, marginBottom: 6 }}>
            GET STARTED
          </div>
          <div style={{ fontSize: 20, fontWeight: 800, color: C.navy, letterSpacing: -0.4 }}>
            Set up your account
          </div>
        </div>
        <div style={{ fontSize: 13, fontWeight: 700, color: C.text2 }}>
          {completed} of {steps.length} done
        </div>
      </div>

      <div style={{
        background: C.cream, borderRadius: 4, height: 6, marginBottom: 24, overflow: "hidden",
      }}>
        <div style={{
          background: C.green, height: "100%",
          width: `${(completed / steps.length) * 100}%`,
          transition: "width 0.3s",
        }} />
      </div>

      <div>
        {steps.map((s) => (
          <div key={s.id} style={{
            display: "flex", alignItems: "center", gap: 14,
            padding: "14px 0", borderBottom: `1px solid ${C.line}`,
          }}>
            <div style={{
              width: 24, height: 24, borderRadius: 12,
              background: s.done ? C.green : C.cream,
              border: s.done ? "none" : `2px solid ${C.border}`,
              color: C.white, fontSize: 13, fontWeight: 800,
              display: "flex", alignItems: "center", justifyContent: "center",
              flexShrink: 0,
            }}>
              {s.done && "✓"}
            </div>
            <div style={{ flex: 1 }}>
              <div style={{
                fontSize: 14, fontWeight: 600,
                color: s.done ? C.text3 : C.navy,
                textDecoration: s.done ? "line-through" : "none",
              }}>
                {s.label}
              </div>
            </div>
            {!s.done && s.action && (
              s.to && !s.disabled ? (
                <Link to={s.to} style={{
                  background: C.red, color: C.white,
                  border: "none", padding: "8px 14px", borderRadius: 6,
                  fontSize: 12, fontWeight: 700, cursor: "pointer",
                  textDecoration: "none", display: "inline-block",
                }}>
                  {s.action}
                </Link>
              ) : s.onClick ? (
                <button type="button" onClick={s.onClick} style={{
                  background: C.red, color: C.white,
                  border: "none", padding: "8px 14px", borderRadius: 6,
                  fontSize: 12, fontWeight: 700, cursor: "pointer",
                  fontFamily: "inherit",
                }}>
                  {s.action}
                </button>
              ) : (
                <button disabled={s.disabled} style={{
                  background: s.disabled ? C.sand : C.red, color: s.disabled ? C.text4 : C.white,
                  border: "none", padding: "8px 14px", borderRadius: 6,
                  fontSize: 12, fontWeight: 700, cursor: s.disabled ? "not-allowed" : "pointer",
                }}>
                  {s.action}
                </button>
              )
            )}
          </div>
        ))}
      </div>

      {/* Verify modals */}
      <VerifyModal
        open={verifyModal === "phone"}
        onClose={() => setVerifyModal(null)}
        kind="phone"
        identifier={merchant?.phone}
        onVerified={onReload}
      />
      <VerifyModal
        open={verifyModal === "email"}
        onClose={() => setVerifyModal(null)}
        kind="email"
        identifier={merchant?.email}
        onVerified={onReload}
      />
    </div>
  );
}

function RecentOrders({ orders }) {
  return (
    <div style={{
      background: C.white, borderRadius: 12,
      border: `1px solid ${C.line}`, overflow: "hidden",
    }}>
      <div style={{ padding: "20px 24px", borderBottom: `1px solid ${C.line}`, display: "flex", justifyContent: "space-between", alignItems: "center" }}>
        <div style={{ fontSize: 18, fontWeight: 800, color: C.navy, letterSpacing: -0.3 }}>
          Recent shipments
        </div>
        <Link to="#" style={{ fontSize: 13, color: C.red, fontWeight: 700, textDecoration: "none" }}>
          View all →
        </Link>
      </div>
      <table style={{ width: "100%", borderCollapse: "collapse" }}>
        <thead>
          <tr style={{ background: C.cream }}>
            <Th>Order #</Th>
            <Th>To</Th>
            <Th>Status</Th>
            <Th align="right">Amount</Th>
          </tr>
        </thead>
        <tbody>
          {orders.slice(0, 8).map((o, i) => (
            <tr key={o.id || i} style={{ borderTop: `1px solid ${C.line}` }}>
              <Td>
                <span style={{ fontWeight: 700, color: C.navy, fontFamily: "monospace", fontSize: 12 }}>
                  {o.order_number || `SS-${String(i).padStart(6, "0")}`}
                </span>
              </Td>
              <Td>
                {o.receiver_name || "—"}
                <div style={{ fontSize: 11, color: C.text3, marginTop: 2 }}>
                  {o.receiver_city || ""} {o.receiver_pincode ? `(${o.receiver_pincode})` : ""}
                </div>
              </Td>
              <Td>
                <StatusPill status={o.status} />
              </Td>
              <Td align="right">
                <span style={{ fontWeight: 700, color: C.navy }}>
₹{o.total_charge_paise ? Math.round(o.total_charge_paise / 100) : "—"}

                </span>
              </Td>
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
}

const STATUS_COLORS = {
  draft: { bg: "#F3F4F6", color: "#4B5563" },
  booked: { bg: "#DBEAFE", color: "#1E40AF" },
  picked_up: { bg: "#FEF3C7", color: "#92400E" },
  in_transit: { bg: "#FEF3C7", color: "#92400E" },
  out_for_delivery: { bg: "#FED7AA", color: "#9A3412" },
  delivered: { bg: "#D1FAE5", color: "#065F46" },
  rto: { bg: "#FEE2E2", color: "#991B1B" },
  cancelled: { bg: "#F3F4F6", color: "#6B7280" },
};

function StatusPill({ status }) {
  const s = STATUS_COLORS[status] || STATUS_COLORS.draft;
  const label = (status || "draft").replace(/_/g, " ");
  return (
    <span style={{
      display: "inline-block",
      background: s.bg, color: s.color,
      padding: "4px 10px", borderRadius: 12,
      fontSize: 11, fontWeight: 700,
      textTransform: "capitalize",
    }}>
      {label}
    </span>
  );
}

function Th({ children, align = "left" }) {
  return (
    <th style={{
      textAlign: align, padding: "12px 24px",
      fontSize: 11, color: C.text3, fontWeight: 700,
      letterSpacing: 1, textTransform: "uppercase",
    }}>
      {children}
    </th>
  );
}

function Td({ children, align = "left" }) {
  return (
    <td style={{
      textAlign: align, padding: "16px 24px",
      fontSize: 14, color: C.text,
    }}>
      {children}
    </td>
  );
}

function ActionTile({ title, desc, to, disabled }) {
  const sharedStyle = {
    background: C.white, border: `1px solid ${C.line}`,
    borderRadius: 10, padding: 18, textAlign: "left",
    cursor: disabled ? "not-allowed" : "pointer",
    opacity: disabled ? 0.5 : 1,
    fontFamily: "inherit",
    transition: "all 0.15s",
    textDecoration: "none", display: "block",
  };
  const content = (
    <>
      <div style={{ fontSize: 14, fontWeight: 700, color: C.navy, letterSpacing: -0.2 }}>{title}</div>
      <div style={{ fontSize: 12, color: C.text3, marginTop: 4 }}>{desc}</div>
    </>
  );
  const hover = (e, on) => {
    if (disabled) return;
    e.currentTarget.style.borderColor = on ? C.red : C.line;
    e.currentTarget.style.transform = on ? "translateY(-2px)" : "translateY(0)";
  };
  if (disabled || !to) {
    return <div style={sharedStyle} onMouseOver={(e) => hover(e, true)} onMouseOut={(e) => hover(e, false)}>{content}</div>;
  }
  return (
    <Link to={to} style={sharedStyle} onMouseOver={(e) => hover(e, true)} onMouseOut={(e) => hover(e, false)}>
      {content}
    </Link>
  );
}
